import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export type ThemeMode = 'dark' | 'light';
export type Language = 'en' | 'es' | 'fr' | 'de' | 'pt';

interface ThemeContextValue {
  theme: ThemeMode;
  language: Language;
  setTheme: (theme: ThemeMode) => void;
  setLanguage: (lang: Language) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

const THEME_STORAGE_KEY = 'momentum-theme';
const LANG_STORAGE_KEY = 'momentum-language';
const ACCOUNT_API_BASE = process.env.REACT_APP_AccountAMP_Url || 'https://ampapi.insurancegig.com';

const updateThemeOnServer = (newTheme: ThemeMode) => {
  const authToken = localStorage.getItem('authToken');
  if (!authToken) return;
  const themeMode = newTheme === 'dark' ? 1 : 2;
  fetch(`${ACCOUNT_API_BASE}/api/channelmanagement/updatetheme?themeMode=${themeMode}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${authToken}` },
  }).catch(() => {});
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    // When source is Rate, always force light theme
    if (localStorage.getItem('sourceTool') === 'Rate') return 'light';
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return (stored === 'light' || stored === 'dark') ? stored : 'dark';
  });

  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    return (stored as Language) || 'en';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  // Re-apply theme from localStorage after login (themeMode comes from server response)
  useEffect(() => {
    const handleAuthUpdate = () => {
      // When source is Rate, always force light theme
      if (localStorage.getItem('sourceTool') === 'Rate') {
        setThemeState('light');
        return;
      }
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') {
        setThemeState(stored);
      }
    };
    window.addEventListener('authTokenUpdated', handleAuthUpdate);
    return () => window.removeEventListener('authTokenUpdated', handleAuthUpdate);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-lang', language);
    localStorage.setItem(LANG_STORAGE_KEY, language);
  }, [language]);

  const setTheme = useCallback((newTheme: ThemeMode) => {
    setThemeState(newTheme);
    updateThemeOnServer(newTheme);
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const value: ThemeContextValue = {
    theme,
    language,
    setTheme,
    setLanguage,
    toggleTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
